import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  DataTable,
  Button,
  Modal,
  TextField,
  Select,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Spinner,
  EmptyState,
  Collapsible,
  Popover,
  ActionList
} from '@shopify/polaris';
import { adminApi } from '../../services/api/adminApi';

export const TenantsManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [filters, setFilters] = useState({ status: 'all', environment: '', provider: '' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importStrategy, setImportStrategy] = useState('replace');
  const [importPreview, setImportPreview] = useState(null);
  const [exportingTenantId, setExportingTenantId] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    environment: 'dev',
    ownerId: '',
    integrations: {
      frontend: { provider: '' },
      backend: { provider: '' }
    }
  });
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ownerSearchOpen, setOwnerSearchOpen] = useState(false);
  const [ownerSearchValue, setOwnerSearchValue] = useState('');

  // Fetch tenants
  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getTenants({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (response.success) {
        setTenants(response.tenants || []);
        setPagination(response.pagination || pagination);
      } else {
        setError(response.error || 'Failed to fetch tenants');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  // Fetch users for owner selection
  const fetchUsers = useCallback(async () => {
    try {
      const response = await adminApi.getUsers({ status: 'active', limit: 1000 });
      if (response.success) {
        setUsers(response.users || []);
      }
    } catch (err) {
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    if (createModalOpen || editModalOpen) {
      fetchUsers();
    }
  }, [createModalOpen, editModalOpen, fetchUsers]);

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      environment: 'dev',
      ownerId: '',
      integrations: {
        frontend: { provider: '' },
        backend: { provider: '' }
      }
    });
    setOwnerSearchValue('');
    setSelectedTenant(null);
    setCreateModalOpen(true);
  };

  const handleEdit = (tenant) => {
    const owner = users.find(u => u.id === tenant.owner?.id);
    setFormData({
      name: tenant.name,
      description: tenant.description || '',
      environment: tenant.environment,
      ownerId: tenant.owner.id,
      integrations: tenant.integrations || {
        frontend: { provider: '' },
        backend: { provider: '' }
      }
    });
    setOwnerSearchValue(owner ? `${owner.name} (${owner.email})` : '');
    setSelectedTenant(tenant);
    setEditModalOpen(true);
  };

  const handleDelete = (tenant) => {
    setSelectedTenant(tenant);
    setDeleteModalOpen(true);
  };

  const handleExport = async (tenant) => {
    try {
      setExportingTenantId(tenant.id);
      const result = await adminApi.exportTenantConfigurations(tenant.id);
      setError(null);
      // Show success message
      setTimeout(() => setExportingTenantId(null), 1000);
    } catch (err) {
      setError(err.message || 'Failed to export configurations');
      setExportingTenantId(null);
    }
  };

  const handleImport = (tenant) => {
    setSelectedTenant(tenant);
    setImportFile(null);
    setImportPreview(null);
    setImportStrategy('replace');
    setImportModalOpen(true);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFile(file);

    // For encrypted files, we can't preview without decryption
    // But we can show file info
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension === 'cartnxs' || fileExtension === 'cnx') {
      setImportPreview({
        filename: file.name,
        size: file.size,
        type: 'encrypted',
        message: 'Encrypted file - metadata will be shown after upload'
      });
    } else if (fileExtension === 'json') {
      // Try to read JSON for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setImportPreview({
            filename: file.name,
            size: file.size,
            type: 'json',
            metadata: data.metadata,
            tenantConfigs: data.tenantConfigs?.length || 0,
            flowConfigs: data.flowConfigs?.length || 0
          });
        } catch (err) {
          setImportPreview({
            filename: file.name,
            size: file.size,
            type: 'json',
            error: 'Invalid JSON format'
          });
        }
      };
      reader.readAsText(file);
    }
  };

  const handleSubmitImport = async () => {
    if (!importFile || !selectedTenant) return;

    try {
      setImporting(true);
      setError(null);
      const result = await adminApi.importTenantConfigurations(
        selectedTenant.id,
        importFile,
        importStrategy
      );

      if (result.success) {
        setImportModalOpen(false);
        setImportFile(null);
        setImportPreview(null);
        fetchTenants();
        // Show success message
        setError(null);
      } else {
        setError(result.error || 'Failed to import configurations');
      }
    } catch (err) {
      setError(err.message || 'Failed to import configurations');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmitCreate = async () => {
    try {
      setSubmitting(true);
      const response = await adminApi.createTenant(formData);
      if (response.success) {
        setCreateModalOpen(false);
        fetchTenants();
      } else {
        setError(response.error || 'Failed to create tenant');
      }
    } catch (err) {
      setError(err.message || 'Failed to create tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async () => {
    try {
      setSubmitting(true);
      const response = await adminApi.updateTenant(selectedTenant.id, formData);
      if (response.success) {
        setEditModalOpen(false);
        fetchTenants();
      } else {
        setError(response.error || 'Failed to update tenant');
      }
    } catch (err) {
      setError(err.message || 'Failed to update tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setSubmitting(true);
      const response = await adminApi.deleteTenant(selectedTenant.id);
      if (response.success) {
        setDeleteModalOpen(false);
        fetchTenants();
      } else {
        setError(response.error || 'Failed to delete tenant');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { status: 'success', children: 'Active' },
      inactive: { status: 'info', children: 'Inactive' },
      maintenance: { status: 'warning', children: 'Maintenance' },
      deleted: { status: 'critical', children: 'Deleted' }
    };
    return <Badge {...(statusMap[status] || { children: status })} />;
  };

  const toggleRow = (tenantId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(tenantId)) {
      newExpanded.delete(tenantId);
    } else {
      newExpanded.add(tenantId);
    }
    setExpandedRows(newExpanded);
  };

  const getEnvironmentBadge = (env) => {
    const envMap = {
      dev: { tone: 'info', children: 'Dev' },
      staging: { tone: 'warning', children: 'Staging' },
      prod: { tone: 'success', children: 'Prod' }
    };
    return <Badge {...(envMap[env] || { children: env })} />;
  };

  const getUserRoleBadge = (role) => {
    const roleMap = {
      owner: { tone: 'success', children: 'Owner' },
      admin: { tone: 'info', children: 'Admin' },
      user: { tone: 'default', children: 'User' },
      viewer: { tone: 'subdued', children: 'Viewer' }
    };
    return <Badge {...(roleMap[role] || { tone: 'default', children: role })} />;
  };

  const tableRows = tenants.map(tenant => {
    const isExpanded = expandedRows.has(tenant.id);
    return [
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Button 
          plain 
          onClick={() => toggleRow(tenant.id)}
          ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? '▼' : '▶'}
        </Button>
        {tenant.name}
      </div>,
      getEnvironmentBadge(tenant.environment),
      getStatusBadge(tenant.status),
      tenant.integrations?.frontend?.provider || '-',
      tenant.integrations?.backend?.provider || '-',
      tenant.owner?.name || '-',
      tenant.memberCount?.toString() || '0',
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button size="micro" onClick={() => handleEdit(tenant)}>Edit</Button>
        <Button size="micro" onClick={() => handleExport(tenant)} loading={exportingTenantId === tenant.id}>Export</Button>
        <Button size="micro" onClick={() => handleImport(tenant)}>Import</Button>
        <Button size="micro" destructive onClick={() => handleDelete(tenant)}>Delete</Button>
      </div>
    ];
  });

  const tableHeadings = [
    'Name',
    'Environment',
    'Status',
    'E-Com Platform',
    'ERP Platform',
    'Owner',
    'Users',
    'Actions'
  ];

  return (
    <BlockStack gap="loose">
      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Card>
        <BlockStack gap="loose">
          <InlineStack align="space-between">
            <Text variant="headingMd" fontWeight="bold">Tenants</Text>
            <Button primary onClick={handleCreate}>Create Tenant</Button>
          </InlineStack>

          <InlineStack gap="loose" align="start">
            <div style={{ minWidth: '200px', width: '200px', marginRight: '24px' }}>
              <Select
                label="Status"
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'Active', value: 'active' },
                  { label: 'Inactive', value: 'inactive' },
                  { label: 'Maintenance', value: 'maintenance' }
                ]}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
              />
            </div>
            <div style={{ minWidth: '200px', width: '200px' }}>
              <Select
                label="Environment"
                options={[
                  { label: 'All', value: '' },
                  { label: 'Dev', value: 'dev' },
                  { label: 'Staging', value: 'staging' },
                  { label: 'Prod', value: 'prod' }
                ]}
                value={filters.environment}
                onChange={(value) => setFilters({ ...filters, environment: value })}
              />
            </div>
          </InlineStack>

          {loading ? (
            <BlockStack align="center">
              <Spinner size="large" />
            </BlockStack>
          ) : tenants.length === 0 ? (
            <EmptyState
              heading="No tenants found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Create your first tenant to get started.</p>
            </EmptyState>
          ) : (
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                    {tableHeadings.map((heading, idx) => (
                      <th
                        key={idx}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontWeight: 600,
                          fontSize: '13px',
                          color: '#202223'
                        }}
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tenant) => {
                    const isExpanded = expandedRows.has(tenant.id);
                    return (
                      <React.Fragment key={tenant.id}>
                        <tr
                          style={{
                            borderBottom: '1px solid #e1e3e5',
                            backgroundColor: isExpanded ? '#f6f6f7' : 'transparent',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Button
                                plain
                                onClick={() => toggleRow(tenant.id)}
                                ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
                              >
                                {isExpanded ? '▼' : '▶'}
                              </Button>
                              {tenant.name}
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>{getEnvironmentBadge(tenant.environment)}</td>
                          <td style={{ padding: '12px 16px' }}>{getStatusBadge(tenant.status)}</td>
                          <td style={{ padding: '12px 16px' }}>{tenant.integrations?.frontend?.provider || '-'}</td>
                          <td style={{ padding: '12px 16px' }}>{tenant.integrations?.backend?.provider || '-'}</td>
                          <td style={{ padding: '12px 16px' }}>{tenant.owner?.name || '-'}</td>
                          <td style={{ padding: '12px 16px' }}>{tenant.memberCount?.toString() || '0'}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <Button size="micro" onClick={() => handleEdit(tenant)}>Edit</Button>
                              <Button size="micro" onClick={() => handleExport(tenant)} loading={exportingTenantId === tenant.id}>Export</Button>
                              <Button size="micro" onClick={() => handleImport(tenant)}>Import</Button>
                              <Button size="micro" destructive onClick={() => handleDelete(tenant)}>Delete</Button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && tenant.users && tenant.users.length > 0 && (
                          <tr>
                            <td colSpan={8} style={{ padding: '0', borderBottom: '1px solid #e1e3e5' }}>
                              <Collapsible
                                open={isExpanded}
                                id={`user-list-${tenant.id}`}
                                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                              >
                                <div style={{ 
                                  padding: '16px 48px',
                                  backgroundColor: '#fafbfb',
                                  borderLeft: '3px solid #008060'
                                }}>
                                  <BlockStack gap="base">
                                    <Text variant="headingSm" fontWeight="semibold" color="subdued">
                                      Users with Access ({tenant.users.length})
                                    </Text>
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                      gap: '12px'
                                    }}>
                                      {tenant.users.map(user => (
                                        <Card key={user.id} background="bg-surface-secondary">
                                          <BlockStack gap="tight">
                                            <BlockStack gap="extraTight">
                                              <Text fontWeight="semibold">{user.name}</Text>
                                              <Text tone="subdued" fontSize="bodyMd">{user.email}</Text>
                                              <InlineStack gap="tight" blockAlign="center">
                                                {getUserRoleBadge(user.role)}
                                              </InlineStack>
                                            </BlockStack>
                                          </BlockStack>
                                        </Card>
                                      ))}
                                    </div>
                                  </BlockStack>
                                </div>
                              </Collapsible>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </BlockStack>
      </Card>

      {/* Create Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setOwnerSearchOpen(false);
        }}
        title="Create Tenant"
        primaryAction={{
          content: 'Create',
          onAction: handleSubmitCreate,
          loading: submitting
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setCreateModalOpen(false);
              setOwnerSearchOpen(false);
            }
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="loose">
            <TextField
              label="Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="off"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              multiline={3}
              autoComplete="off"
            />
            <Select
              label="Environment"
              options={[
                { label: 'Dev', value: 'dev' },
                { label: 'Staging', value: 'staging' },
                { label: 'Prod', value: 'prod' }
              ]}
              value={formData.environment}
              onChange={(value) => setFormData({ ...formData, environment: value })}
            />
            <div>
              <Popover
                active={ownerSearchOpen}
                activator={
                  <TextField
                    label="Owner"
                    value={ownerSearchValue}
                    onChange={(value) => {
                      setOwnerSearchValue(value);
                      setOwnerSearchOpen(true);
                      if (!value) {
                        setFormData({ ...formData, ownerId: '' });
                      }
                    }}
                    onFocus={() => setOwnerSearchOpen(true)}
                    placeholder="Search for user..."
                    autoComplete="off"
                  />
                }
                onClose={() => setOwnerSearchOpen(false)}
                preferredAlignment="left"
              >
                <ActionList
                  items={users.length > 0 ? users
                    .filter(user => {
                      if (!ownerSearchValue) return true;
                      const searchLower = ownerSearchValue.toLowerCase();
                      return (
                        user.name.toLowerCase().includes(searchLower) ||
                        user.email.toLowerCase().includes(searchLower)
                      );
                    })
                    .map(user => ({
                      content: `${user.name} (${user.email})`,
                      onAction: () => {
                        setFormData({ ...formData, ownerId: user.id });
                        setOwnerSearchValue(`${user.name} (${user.email})`);
                        setOwnerSearchOpen(false);
                      },
                      active: formData.ownerId === user.id
                    })) : [{ content: 'No users found', disabled: true }]}
                />
              </Popover>
            </div>
            <Select
              label="E-Com Platform"
              options={[
                { label: 'None', value: '' },
                { label: 'Adobe Commerce', value: 'adobe' },
                { label: 'Shopify', value: 'shopify' },
                { label: 'Wayfair', value: 'wayfair' },
                { label: 'Wayfair Agent', value: 'wayfairagent' }
              ]}
              value={formData.integrations.frontend.provider}
              onChange={(value) => setFormData({
                ...formData,
                integrations: {
                  ...formData.integrations,
                  frontend: { ...formData.integrations.frontend, provider: value }
                }
              })}
            />
            <Select
              label="ERP Platform"
              options={[
                { label: 'None', value: '' },
                { label: 'NetSuite', value: 'netsuite' },
                { label: 'SAGE 300', value: 'sage' },
                { label: 'QuickBooks Online', value: 'quickbooks' },
                { label: 'Business Central', value: 'bc' }
              ]}
              value={formData.integrations.backend.provider}
              onChange={(value) => setFormData({
                ...formData,
                integrations: {
                  ...formData.integrations,
                  backend: { ...formData.integrations.backend, provider: value }
                }
              })}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setOwnerSearchOpen(false);
        }}
        title="Edit Tenant"
        primaryAction={{
          content: 'Save',
          onAction: handleSubmitEdit,
          loading: submitting
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setEditModalOpen(false);
              setOwnerSearchOpen(false);
            }
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="loose">
            <TextField
              label="Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="off"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              multiline={3}
              autoComplete="off"
            />
            <Select
              label="Status"
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
                { label: 'Maintenance', value: 'maintenance' }
              ]}
              value={selectedTenant?.status || 'active'}
              onChange={(value) => {
                if (selectedTenant) {
                  setSelectedTenant({ ...selectedTenant, status: value });
                }
              }}
            />
            <div>
              <Popover
                active={ownerSearchOpen}
                activator={
                  <TextField
                    label="Owner"
                    value={ownerSearchValue}
                    onChange={(value) => {
                      setOwnerSearchValue(value);
                      setOwnerSearchOpen(true);
                      if (!value) {
                        setFormData({ ...formData, ownerId: '' });
                      }
                    }}
                    onFocus={() => setOwnerSearchOpen(true)}
                    placeholder="Search for user..."
                    autoComplete="off"
                  />
                }
                onClose={() => setOwnerSearchOpen(false)}
                preferredAlignment="left"
              >
                <ActionList
                  items={users.length > 0 ? users
                    .filter(user => {
                      if (!ownerSearchValue) return true;
                      const searchLower = ownerSearchValue.toLowerCase();
                      return (
                        user.name.toLowerCase().includes(searchLower) ||
                        user.email.toLowerCase().includes(searchLower)
                      );
                    })
                    .map(user => ({
                      content: `${user.name} (${user.email})`,
                      onAction: () => {
                        setFormData({ ...formData, ownerId: user.id });
                        setOwnerSearchValue(`${user.name} (${user.email})`);
                        setOwnerSearchOpen(false);
                      },
                      active: formData.ownerId === user.id
                    })) : [{ content: 'No users found', disabled: true }]}
                />
              </Popover>
            </div>
            <Select
              label="E-Com Platform"
              options={[
                { label: 'None', value: '' },
                { label: 'Adobe Commerce', value: 'adobe' },
                { label: 'Shopify', value: 'shopify' },
                { label: 'Wayfair', value: 'wayfair' },
                { label: 'Wayfair Agent', value: 'wayfairagent' }
              ]}
              value={formData.integrations.frontend.provider}
              onChange={(value) => setFormData({
                ...formData,
                integrations: {
                  ...formData.integrations,
                  frontend: { ...formData.integrations.frontend, provider: value }
                }
              })}
            />
            <Select
              label="ERP Platform"
              options={[
                { label: 'None', value: '' },
                { label: 'NetSuite', value: 'netsuite' },
                { label: 'SAGE 300', value: 'sage' },
                { label: 'QuickBooks Online', value: 'quickbooks' },
                { label: 'Business Central', value: 'bc' }
              ]}
              value={formData.integrations.backend.provider}
              onChange={(value) => setFormData({
                ...formData,
                integrations: {
                  ...formData.integrations,
                  backend: { ...formData.integrations.backend, provider: value }
                }
              })}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Tenant"
        primaryAction={{
          content: 'Delete',
          onAction: handleConfirmDelete,
          loading: submitting,
          destructive: true
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDeleteModalOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <Text>
            Are you sure you want to delete tenant "{selectedTenant?.name}"? This action will soft delete the tenant.
          </Text>
        </Modal.Section>
      </Modal>

      {/* Import Modal */}
      <Modal
        open={importModalOpen}
        onClose={() => {
          setImportModalOpen(false);
          setImportFile(null);
          setImportPreview(null);
        }}
        title="Import Tenant Configurations"
        primaryAction={{
          content: 'Import',
          onAction: handleSubmitImport,
          loading: importing,
          disabled: !importFile
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setImportModalOpen(false);
              setImportFile(null);
              setImportPreview(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="loose">
            <Text>
              Import configurations from an exported file (.cartnxs or .json) into tenant "{selectedTenant?.name}".
            </Text>

            <div>
              <input
                type="file"
                accept=".cartnxs,.cnx,.json"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="import-file-input"
              />
              <Button onClick={() => document.getElementById('import-file-input').click()}>
                {importFile ? 'Change File' : 'Select File'}
              </Button>
              {importFile && (
                <div style={{ marginTop: '8px' }}>
                  <Text variant="bodyMd">Selected: {importFile.name}</Text>
                  <Text variant="bodySm" tone="subdued">
                    Size: {(importFile.size / 1024).toFixed(2)} KB
                  </Text>
                </div>
              )}
            </div>

            {importPreview && (
              <Card sectioned>
                <BlockStack gap="base">
                  <Text variant="headingSm" fontWeight="bold">Import Preview</Text>
                  {importPreview.type === 'encrypted' && (
                    <Text tone="subdued">{importPreview.message}</Text>
                  )}
                  {importPreview.type === 'json' && importPreview.metadata && (
                    <BlockStack gap="tight">
                      <Text>Original Tenant: {importPreview.metadata.tenantName || 'Unknown'}</Text>
                      <Text>Environment: {importPreview.metadata.environment || 'Unknown'}</Text>
                      <Text>Exported At: {new Date(importPreview.metadata.exportedAt).toLocaleString()}</Text>
                      <Text>Exported By: {importPreview.metadata.exportedBy || 'Unknown'}</Text>
                      <Text>Tenant Configs: {importPreview.tenantConfigs || 0}</Text>
                      <Text>Flow Configs: {importPreview.flowConfigs || 0}</Text>
                    </BlockStack>
                  )}
                  {importPreview.error && (
                    <Banner status="critical">{importPreview.error}</Banner>
                  )}
                </BlockStack>
              </Card>
            )}

            <Select
              label="Import Strategy"
              options={[
                { label: 'Replace All', value: 'replace', helpText: 'Replace all existing configs with imported ones' },
                { label: 'Merge', value: 'merge', helpText: 'Skip existing configs, add new ones' },
                { label: 'Update', value: 'update', helpText: 'Update existing configs, add new ones' }
              ]}
              value={importStrategy}
              onChange={setImportStrategy}
            />

            <Banner status="info">
              Exported file is encrypted. Use same server key for import.
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
};
